<template>
  <header
    class="team-area-header container maxWidth-md"
    :class="{
      'team-lead-header': props.currentTab === 'TeamLeadArea',
      'team-dashboard-header': props.currentTab === 'TeamDashboard',
      'my-dashboard-header': props.currentTab === 'MyDashboard',
      'my-development-header': props.currentTab === 'MyDevelopment',
    }"
  >
    <div class="header-tabs">
      <div
        class="team-lead-tab"
        :class="{ 'locked-levels-tab': !userIsTeamLeader }"
        @click="emit('update:selectedView', 'TeamLeadArea')"
        @keydown.space="emit('update:selectedView', 'TeamLeadArea')"
        data-test="team-lead-tab"
        tabindex="0"
      >
        <span class="img-container">
          <img src="../../assets/team-area/superhero-icon.svg" alt="Team Lead icon" />
        </span>
        <span class="tab-text">Team Lead Area</span>
      </div>
      <div
        class="dashboard-tab"
        @click="emit('update:selectedView', 'TeamDashboard')"
        @keydown.space="emit('update:selectedView', 'TeamDashboard')"
        data-test="team-dashboard-tab"
        tabindex="0"
      >
        <span class="img-container">
          <img src="../../assets/team-area/dashboard-icon.svg" alt="Team Dashboard icon" /></span
        ><span class="tab-text">Team Dashboard</span>
      </div>
      <div
        class="my-development-tab"
        @click="emit('update:selectedView', 'MyDevelopment')"
        @keydown.space="emit('update:selectedView', 'MyDevelopment')"
        data-test="my-development-tab"
        tabindex="0"
      >
        <span class="img-container">
          <img src="../../assets/team-area/grow-leaves-icon.svg" alt="My Development icon" /></span
        ><span class="tab-text">My Development</span>
      </div>
      <div
        class="my-dashboard-tab"
        @click="emit('update:selectedView', 'MyDashboard')"
        @keydown.space="emit('update:selectedView', 'MyDashboard')"
        data-test="my-dashboard-tab"
        tabindex="0"
      >
        <span class="img-container"
          ><img src="../../assets/team-area/dashboard-icon.svg" alt="My Dashboard icon" /></span
        ><span class="tab-text">My Dashboard</span>
      </div>
    </div>

    <!-- Team Lead View -->
    <div
      v-if="props.currentTab === 'TeamLeadArea'"
      class="header-content"
      :class="{ 'locked-levels': !userIsTeamLeader }"
      data-test="team-lead-header"
    >
      <div class="primary-header-content-container">
        <div class="header-section-left">
          <h1>Hello, {{ userIsTeamLeader ? "Team Lead" : firstName }}</h1>
          <p data-test="team-lead-description">{{ subTitleText }}</p>
          <!-- <span><button class="btn btn-primary" type="button">Set up a new team</button></span> -->
        </div>
        <div class="header-section-right">
          <img
            v-if="heroGender === 'male'"
            src="../../assets/team-area/jyranian-superhero-cropped.png"
            alt="Team Lead Superhero Male"
            data-test="hero-image"
          />
          <img
            v-else
            src="../../assets/team-area/jyranian-superhero-cropped-2.png"
            alt="Team Lead Superhero Female"
            data-test="hero-image"
          />
          <button
            type="button"
            @click="switchHeroGender"
            class="btn btn-switch"
            data-test="hero-image-switch"
          ></button>
        </div>
      </div>
      <a
        v-if="isTeamGoalsSet"
        href="/teamgoals"
        class="btn btn-team-goals btn-thin team-goals-button"
        data-test="goals-document-link"
        >Team Goals
      </a>
    </div>

    <!-- Team Dashboard View -->
    <div
      v-else-if="props.currentTab === 'TeamDashboard'"
      class="header-content"
      data-test="team-dashboard-header"
    >
      <div class="primary-header-content-container">
        <div class="header-section-left">
          <div class="header-section-team-info" data-test="team-name">
            <div class="header-section-left">
              <h1 data-test="team-name-heading">
                {{ teamModel ? teamModel.name : "Setup a team" }}
              </h1>
              <p v-if="!teamModel" class="no-team-subtitle">
                Unlock your Team Dashboard where you'll have access to a Team Solo report, Team
                Viewpoints report, team outcomes and much more.
              </p>
            </div>
            <img
              v-if="teamModel"
              :src="teamImageUrl"
              alt="Team Logo"
              class="team-image-container team-image-container-md"
              data-test="team-image"
            />
          </div>
        </div>
        <div class="header-section-right">
          <div class="guidance-icon-container" id="team-tooltip-target">
            <GuidanceButton
              link="/resources/read/using-gyre-as-a-team-member"
              tooltip-text="How to get the most from my Team Journey"
              tooltip-target="#team-tooltip-target"
              img-width="43px"
            />
          </div>
          <a
            v-if="teamModel"
            :href="'/team/settings/' + teamModel.id"
            class="btn btn-default-settings"
            aria-label="team settings"
            data-test="settings-button"
          ></a>
        </div>
      </div>
      <a
        v-if="isTeamGoalsSet"
        href="/teamgoals"
        class="btn btn-team-goals btn-thin team-goals-button"
        style="margin-top: 0"
        data-test="goals-document-link"
        >Team Goals
      </a>
    </div>

    <!-- My Dashboard View -->
    <div
      v-else-if="props.currentTab === 'MyDashboard'"
      class="header-content"
      data-test="my-dashboard-header"
    >
      <div class="primary-header-content-container">
        <div class="header-section-left">
          <div class="header-section-team-info" data-test="team-name">
            <h1>
              Hello, {{ firstName }}
              <img
                src="../../assets/wave-emoji.png"
                class="wave-emoji"
                alt="wave emoji"
                data-test="wave-emoji"
              />
            </h1>
          </div>
        </div>
        <div class="header-section-right">
          <div class="guidance-icon-container" id="personal-tooltip-target">
            <GuidanceButton
              link="/resources/read/using-gyre-as-an-individual"
              tooltip-text="How to get the most from my Personal Journey"
              tooltip-target="#personal-tooltip-target"
              img-width="43px"
            />
          </div>
        </div>
      </div>
      <a
        href="/resources/read/manual-of-me"
        class="btn btn-team-goals btn-thin team-goals-button"
        style="margin-top: 0"
        data-test="manual-of-me-document-link"
        >Manual of Me
      </a>
    </div>

    <!-- My Development View -->
    <div
      v-else-if="props.currentTab === 'MyDevelopment'"
      class="header-content"
      data-test="my-development-header"
    >
      <div class="primary-header-content-container">
        <div class="header-section-left">
          <div class="header-section-team-info" data-test="user-name">
            <h1>
              Hello, {{ firstName }}
              <img
                src="../../assets/wave-emoji.png"
                class="wave-emoji"
                alt="wave emoji"
                data-test="wave-emoji"
              />
            </h1>
          </div>
        </div>
      </div>
      <a
        href="/resources/read/manual-of-me"
        class="btn btn-team-goals btn-thin team-goals-button"
        style="margin-top: 0"
        data-test="manual-of-me-document-link"
        >Manual of Me
      </a>
    </div>
  </header>
</template>

<script setup lang="ts">
import { Ref, computed, inject, ref, watch } from "vue";
import GuidanceButton from "../guidance-button.vue";
import { HomeView } from "../../views/home-page.vue";
import localStorageService from "../../services/utilities/local-storage-service";
import { TeamApiModel } from "../../api-models";
import { csContext } from "../../services/cs-context";

type HeroGender = "male" | "female";

const teamModel = inject("teamModel") as Ref<TeamApiModel>;
const isTeamGoalsSet = inject("isTeamGoalsSet") as Ref<boolean>;

const emit = defineEmits(["update:selectedView"]);

const props = defineProps<{
  currentTab?: HomeView;
}>();

const firstName = computed(() => csContext.user.firstName);

const userIsTeamLeader = inject("userIsTeamLeader") as Ref<boolean>;

const heroGender = ref<HeroGender>(localStorageService.get<HeroGender>("heroGender") ?? "male");

const subTitleText = computed(() => {
  return userIsTeamLeader.value
    ? "You're in the right place to develop your team."
    : "You need to set up a team to unlock this area.";
});

function switchHeroGender() {
  heroGender.value = heroGender.value === "male" ? "female" : "male";
}

watch(heroGender, () => {
  localStorageService.save("heroGender", heroGender.value);
});

const teamImageUrl = computed(() => {
  if (teamModel.value?.id) {
    return (
      localStorageService.get<string>(`teamImageUrl-${teamModel.value.id}`) ??
      teamModel.value?.imageUrl
    );
  }
  return "";
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.team-area-header {
  padding: 0;
  position: relative;
  margin: 0 20px;

  @media (min-width: @screen-sm) {
    margin: 0 auto;
  }
}

.header-tabs {
  display: flex;
  align-items: center;
  position: absolute;
  z-index: 0;
  top: -40px;
  left: 0;

  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 15px 20px;
    border-radius: 10px 10px 0 0;
    cursor: pointer;
    font-size: 16px;
    color: @form-text-color;
    background-color: #eeeeee;
    transition: transform 0.2s ease;

    .img-container,
    .tab-text {
      filter: opacity(0.6);
    }
  }

  .img-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 3px;
    border-radius: 50%;
    padding: 4px;
  }

  @tab-img-size: 20px;

  img {
    height: @tab-img-size;
    width: @tab-img-size;
  }
}

.my-dashboard-tab,
.dashboard-tab {
  .img-container {
    background-color: #ffffff;
  }
}

.primary-header-content-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 70px;
}

.header-content {
  border-radius: 10px;
  background-color: #ffffff;
  position: relative;
  z-index: 1;
  padding: 15px 30px;
  min-height: 100px;

  h1 {
    margin: 0;
  }

  p {
    margin: 0;
  }

  .team-goals-button {
    align-self: unset;
  }
}

.header-section-team-info {
  display: flex;
  align-items: center;

  h1 {
    margin-right: 10px;
  }
}

.no-team-subtitle {
  font-size: 16px;
  color: @form-text-color;
}

.unselected-tab-image-styles {
  &:hover {
    .img-container {
      filter: opacity(1);
    }
  }
}

.unselected-tab-styles {
  &:hover {
    .tab-text {
      filter: opacity(1);
    }

    transform: translateY(-5px);
  }
}

.team-lead-header .team-lead-tab,
.team-dashboard-header .dashboard-tab,
.my-dashboard-header .my-dashboard-tab,
.my-development-header .my-development-tab {
  border: 1px solid @btn-default-border;
  background-color: #ffffff;

  .img-container,
  .tab-text {
    filter: opacity(1);
  }
}

.team-lead-header {
  .dashboard-tab,
  .my-dashboard-tab,
  .my-development-tab {
    .unselected-tab-styles();
  }

  .dashboard-tab,
  .my-dashboard-tab {
    .unselected-tab-image-styles();
  }
}

.team-dashboard-header {
  .team-lead-tab,
  .my-dashboard-tab,
  .my-development-tab {
    .unselected-tab-styles();
  }

  .my-dashboard-tab {
    .unselected-tab-image-styles();
  }
}

.my-dashboard-header {
  .dashboard-tab,
  .team-lead-tab,
  .my-development-tab {
    .unselected-tab-styles();
  }

  .dashboard-tab {
    .unselected-tab-image-styles();
  }
}

.my-development-header {
  .dashboard-tab,
  .team-lead-tab,
  .my-dashboard-tab {
    .unselected-tab-styles();
  }

  .dashboard-tab,
  .my-dashboard-tab {
    .unselected-tab-image-styles();
  }
}

.team-dashboard-header {
  .header-section-right {
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 1rem;
  }
}

.team-lead-tab .img-container {
  background-color: @charismatic-color;
}

.my-development-tab .img-container {
  background-color: @customreport-color;
}

.header-tabs .team-lead-tab,
.header-tabs .my-development-tab {
  .img-container {
    filter: opacity(1);
  }
}

.my-development-header .my-development-tab {
  background-color: @customreport-color;

  border: none;

  .tab-text {
    filter: brightness(200%) opacity(1);
  }

  &:hover {
    color: #ffffff;
  }
}

.team-lead-header .team-lead-tab {
  background-color: @charismatic-color;

  border: none;

  .tab-text {
    filter: brightness(200%) opacity(1);
  }

  &:hover {
    color: #ffffff;
  }
}

.my-development-header {
  .header-content {
    color: #ffffff;
    background-color: @customreport-color;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  p {
    font-size: 18px;
    font-weight: lighter;
  }
}

.team-lead-header {
  .header-content {
    //max-height: 130px;
    color: #ffffff;
    background-color: @charismatic-color;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  p {
    font-size: 18px;
    font-weight: lighter;
  }

  .header-section-right {
    width: 180px;
  }

  .header-section-right button {
    position: absolute;
    top: 5px;
    right: 5px;
  }

  .header-section-right img {
    position: absolute;
    bottom: 0;
    right: 25px;
    height: 170px;
  }
}

.team-dashboard-header {
  .header-section-right {
    margin-left: 15px;
  }
}

h1 {
  margin: 10px 12px 10px 0;
}

.team-image-container {
  margin: 0;
}

.team-goals-button {
  margin-top: 10px;
}

// SMALLER SCREEN STYLES
@media (max-width: 750px) {
  .header-content {
    p {
      font-size: 14px;
    }

    h1 {
      font-size: 34px;
    }
  }
}

// @media (max-width: 900px) {
//   .team-lead-header {
//     .header-section-right {
//       // width: 90px;

//       img {
//         // height: 100px;
//         right: 12px;
//       }
//     }
//   }
// }

// MAKE THE JYRANIAN SMALLER TO UNBLOCK THE TABS
@media (max-width: @screen-sm-max) {
  .team-lead-header {
    .header-section-right {
      width: 90px;

      img {
        height: 100px;
        right: 12px;
      }
    }
  }
}

// Make the tab text smaller so it doesn't overflow
@media (max-width: @screen-xs-max) {
  .header-tabs {
    & > div {
      font-size: 1.6vw;
    }
  }
}

// MOBILE STYLES
@media (max-width: @screen-xxs-max) {
  .team-area {
    padding-top: 0 !important;
  }

  .team-area-header {
    margin: 0 0 55px;
  }

  .header-tabs {
    z-index: 2;
    top: unset;
    bottom: -50px;
    flex-wrap: wrap;

    @tab-border-radius-size: 30px;

    .team-lead-tab {
      order: 1;
      border-top-right-radius: @tab-border-radius-size;
    }

    .my-development-tab {
      order: 2;
      border-top-left-radius: @tab-border-radius-size;
    }

    .dashboard-tab {
      order: 3;
      border-bottom-right-radius: @tab-border-radius-size;
    }

    .my-dashboard-tab {
      order: 4;
      border-bottom-left-radius: @tab-border-radius-size;
    }

    & > div {
      flex: 1 0 50%;
      max-height: 45px;
      border-radius: 0px;
      padding: 10px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 3vw;

      &:hover {
        transform: none !important;
      }
    }
  }

  .header-content {
    padding: 20px 20px 55px;
    border-radius: 0;
    min-height: 134px;

    p {
      font-size: 3vw;
    }

    h1 {
      font-size: 6.5vw;
    }
  }

  .team-lead-header .header-section-right img {
    bottom: 38px;
  }

  .team-dashboard-header {
    .header-section-right {
      display: none;
    }
  }
}

.mobile-settings-button {
  padding-bottom: 40px;
  margin-top: -10px;

  a::before {
    @size: 50px;
    width: @size;
    height: @size;
  }
}

@keyframes wave {
  from {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(20deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

.wave-emoji {
  animation-name: wave;
  animation-duration: 0.5s;
  animation-delay: 0.5s;
  animation-iteration-count: 2;
  height: 1.1em;
  position: relative;
  left: 5px;
  bottom: 5px;
}
</style>
